import { RegularButton } from "@components/buttons"
import { HookFormInput } from "@components/input/hook-form/HookFormInput"
import { Modal } from "@components/modal"
import { FlexBox } from "@components/wrapper"
import { COUNTRY_CODE } from "@constants/country-code"
import { Listbox, Transition } from "@headlessui/react"
import { yupResolver } from "@hookform/resolvers/yup"
import { InstallmentDuration, InstallmentProgram } from "@interfaces/installment"
import InstallmentServices from "@lib/api/services/installment.services"
import { priceFormatter } from "@lib/functions"
import { useUserData } from "@lib/hooks/useUserData"
import clsx from "clsx"
import { useRouter } from "next/router"
import React, { FC, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { AiOutlineCaretDown, AiOutlineClose } from "react-icons/ai"
import Select, { CSSObjectWithLabel } from "react-select"
import * as Yup from "yup"
import { InstallmentSuccessModal } from "./InstallmentSuccessModal"

export interface InstallmentModalProps {
  onHide: any
  show: boolean
  type: string
}

interface Props {
  user_fullname: string
  user_email: string
  user_phone_number: string
  program_id?: number
  duration?: number
}

const INITIAL_STATE: Props = {
  user_fullname: "",
  user_email: "",
  user_phone_number: "",
}

export const InstallmentModal: FC<InstallmentModalProps> = ({ onHide, show, type }) => {
  const [initialBody, setInitialBody] = useState(INITIAL_STATE)
  const [programOptions, setProgramOptions] = useState<InstallmentProgram[]>([])
  const [installmentDuration, setInstallmentDuration] = useState<InstallmentDuration[]>([])

  const [selectedCode, setSelectedCode] = useState("+62")
  const [selectedProgram, setSelectedProgram] = useState<InstallmentProgram | null>(null)
  const [selectedDuration, setSelectedDuration] = useState<InstallmentDuration | null>(null)
  const [isCustomDuration, setIsCustomDuration] = useState(false)
  const [customDuration, setCustomDuration] = useState<number | null>(null)

  const [successModal, setSuccessModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { tokenData, isLogin } = useUserData()
  const router = useRouter()
  const query = router.query

  const validationSchema = Yup.object()
    .shape({
      user_fullname: Yup.string()
        .required("*Wajib diisi")
        .matches(/^[a-zA-Z-,]+(\s{0,1}[a-zA-Z-, ])*$/, "*Hanya boleh huruf")
        .min(3, "*Minimal 3 karakter")
        .max(50, "*Hanya boleh maksimal 50 karakter"),
      user_email: Yup.string().email("*Email tidak valid").required("*Wajib diisi"),
      user_phone_number: Yup.string()
        .min(10, "*Minimal 10 angka")
        .max(13, "*Maksimal 13 angka")
        .required("*Wajib diisi"),
      program_id: Yup.string().required("*Wajib diisi"),
      ...(type === "Harisenin" && {
        duration: Yup.number()
          .min(1, "*Minimal 1 bulan")
          .max(18, "*Maksimal 18 bulan")
          .required("*Wajib diisi")
          .typeError("*Wajib diisi"),
      }),
    })
    .required()

  const { handleSubmit, reset, control, setValue, formState } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    defaultValues: useMemo(() => {
      return initialBody
    }, [initialBody]),
  })

  const installment = new InstallmentServices()

  useEffect(() => {
    const getUserData = () => {
      const newBody = {
        ...INITIAL_STATE,
        user_email: tokenData?.user_email,
        user_fullname: tokenData?.fullname,
      }
      setInitialBody(newBody)
      reset(newBody)
    }

    if (isLogin) {
      getUserData()
    }
  }, [tokenData, isLogin])

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await installment.getInstallmentData({
          key: "program-price",
          slug: query.slug as string,
          for: type.toLowerCase(),
        })

        if (res.isSuccess) {
          const data = res.getValue() as InstallmentProgram[]
          setProgramOptions(data)
        }

        if (type === "Harisenin") {
          // if (type !== "Danacita") {
          const res2 = await installment.getInstallmentData({
            key: "duration",
            slug: query.slug as string,
            for: "harisenin",
          })

          if (res2.isSuccess) {
            const data = res2.getValue() as InstallmentDuration[]
            setInstallmentDuration(data)
          }
        }
      } catch (error) {
        setProgramOptions([])
        return setInstallmentDuration([])
      }
    }

    getData()
  }, [type])

  const onSubmit = async (data) => {
    try {
      setErrorMessage("")
      setIsSubmitting(true)
      const res = await installment.submitInstallmentData({
        installment_from: type.toLowerCase(),
        program_id: data.program_id,
        user_fullname: data.user_fullname,
        user_phone_number: selectedCode + parseInt(data.user_phone_number),
        ...(type === "Harisenin" && { installment_duration: data?.duration }),
      })

      if (res.isFailure) {
        setErrorMessage("Terjadi Kesalahan. Silahkan Coba lagi")
        return setIsSubmitting(false)
      }

      onHide()
      setSuccessModal(true)
      setErrorMessage("")
      return setIsSubmitting(false)
    } catch (error) {
      setErrorMessage("Terjadi Kesalahan. Silahkan Coba lagi")
      return setIsSubmitting(false)
    }
  }

  const DialCodeSelector = () => {
    return (
      <div className={clsx("relative")}>
        <Select
          options={COUNTRY_CODE}
          key={`my_unique_select_key__${JSON.stringify(selectedCode)}`}
          getOptionLabel={(option) => `${option.name}`}
          getOptionValue={(opt) => `${opt?.dial_code}`}
          formatOptionLabel={(option) => `${option.flag} ${option?.dial_code}`}
          onChange={(opt) => setSelectedCode(opt?.dial_code)}
          value={COUNTRY_CODE.find((c) => c?.dial_code === selectedCode)}
          styles={{
            control: (props): CSSObjectWithLabel =>
              ({
                ...props,
                border: "1px solid rgba(58, 53, 65, 0.23)",
                borderColor: "rgba(58, 53, 65, 0.23)",
                boxShadow: "0 0 0 rgba(58, 53, 65, 0.23)",
                borderRadius: "2px",
                minWidth: "120px",
                minHeight: 0,
                padding: "16px",
                "&:hover": {
                  border: "1px solid rgba(58, 53, 65, 0.23)",
                  boxShadow: "0 0 0 rgba(58, 53, 65, 0.23)",
                },
                "&:focus": {
                  border: "2px solid rgba(58, 53, 65, 0.23)",
                  boxShadow: "0 0 0 rgba(58, 53, 65, 0.23)",
                },
                "@media(max-width: 576px)": {
                  fontSize: "14px",
                  minWidth: "96px",
                  padding: "16px 8px",
                },
              } as CSSObjectWithLabel),
            input: (props): CSSObjectWithLabel =>
              ({
                ...props,
                margin: 0,
                paddingTop: 0,
                paddingBottom: 0,
              } as CSSObjectWithLabel),
            placeholder: (props): CSSObjectWithLabel =>
              ({
                ...props,
                color: "#979797",
                marginLeft: 0,
                marginRight: 0,
              } as CSSObjectWithLabel),
            indicatorSeparator: () => ({
              display: "none",
            }),
            dropdownIndicator: (props): CSSObjectWithLabel =>
              ({
                ...props,
                padding: 0,
              } as CSSObjectWithLabel),
            valueContainer: (props): CSSObjectWithLabel =>
              ({
                ...props,
                padding: "0 5px 0 0",
              } as CSSObjectWithLabel),
            option: (props, { isSelected }): CSSObjectWithLabel =>
              ({
                ...props,
                backgroundColor: isSelected ? "#0DAD8E" : "white",
                "&:active": {
                  backgroundColor: "rgba(13, 173, 142, 0.5)",
                },
                "@media(max-width: 576px)": {
                  fontSize: "12px",
                },
              } as CSSObjectWithLabel),
          }}
        />
      </div>
    )
  }

  function handleChangeProgram(option: InstallmentProgram) {
    setValue("program_id", option.id)
    setSelectedProgram(option)
  }
  function handleChangeDuration(option: InstallmentDuration) {
    if (option.duration !== "Custom Durasi") {
      setValue("duration", option.duration)
      setIsCustomDuration(false)
    } else {
      setValue("duration", null)
      setIsCustomDuration(true)
    }
    setSelectedDuration(option)
  }

  function handleChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.validity.valid) {
      const value = parseInt(e.target.value)
      setCustomDuration(value)
      setValue("duration", value)
    }
  }

  return (
    <>
      <Modal
        show={show}
        onClose={onHide}
        contentPadding="sm:p-8 p-5"
        customCloseButton={
          <button
            className={clsx("absolute sm:text-xl sm:top-9 sm:right-8", "text-lg top-6 right-5")}
            onClick={onHide}
          >
            <AiOutlineClose />
          </button>
        }
        contentClassName="rounded-none sm:min-w-[45vw]"
      >
        <div className="font-semibold sm:text-xl sm-only:w-[90%]">
          Yuk, Isi Datamu untuk Ajukan Cicilan via {type}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="sm:flex relative">
          <div className="flex flex-col gap-5 w-full mt-4">
            <HookFormInput
              control={control}
              name="user_fullname"
              // placeholder="Nama Lengkap"
              label="Nama Lengkap"
            />
            <HookFormInput control={control} name="user_email" label="E-Mail" />

            <FlexBox className={clsx("gap-3")}>
              <DialCodeSelector />
              <HookFormInput
                control={control}
                label="Nomor Whatsapp"
                name="user_phone_number"
                className="w-full"
                type="number"
              />
            </FlexBox>

            <div className="relative">
              <Listbox onChange={handleChangeProgram} value={selectedProgram}>
                {({ open }) => (
                  <div className="relative mt-1">
                    {open ||
                      (selectedProgram && (
                        <div
                          className={clsx(
                            "absolute left-3 -top-2.5 bg-white px-2",
                            "text-xs",
                            formState?.errors?.program_id?.message && "text-red"
                          )}
                        >
                          Program
                        </div>
                      ))}
                    <Listbox.Button
                      className={clsx(
                        "py-4 px-3",
                        "rounded-sm w-full sm-only:text-sm",
                        "flex justify-between items-center",
                        open ? "border-green border-2" : "border-[rgba(58,53,65,0.23)] border",
                        formState?.errors?.program_id?.message && "border-2 !border-red"
                      )}
                    >
                      {!open && (
                        <div className="pl-1.5 sm-only:text-sm text-left">
                          {selectedProgram ? (
                            <span>
                              {selectedProgram.program_title} -{" "}
                              <span className={clsx("text-green font-semibold")}>
                                {priceFormatter(selectedProgram.program_price)}
                              </span>
                            </span>
                          ) : (
                            "Program"
                          )}
                        </div>
                      )}
                      {open && <span className="h-[1.5em]" />}
                      <span className={clsx(open && "text-green")}>
                        <AiOutlineCaretDown />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={React.Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options
                        className={clsx(
                          "w-full bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                          "absolute mt-1 overflow-auto z-[10] pl-0 py-2 sm-only:text-sm",
                          "divide-y divide-[rgba(58,53,65,0.23)]"
                        )}
                      >
                        {programOptions.map((program, idx) => (
                          <Listbox.Option
                            key={idx}
                            value={program}
                            className={({ active, selected }) =>
                              clsx(
                                "py-1.5 px-4 cursor-pointer sm-only:text-sm",
                                selected && "bg-green text-white",
                                active && "bg-green-50"
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span>{program.program_title} - </span>
                                <span
                                  className={clsx(
                                    "text-green font-semibold",
                                    selected ? "text-white" : "text-green"
                                  )}
                                >
                                  {priceFormatter(program.program_price)}
                                </span>
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                )}
              </Listbox>
              {formState?.errors?.program_id?.message && (
                <small className="text-red absolute left-0 bottom-[-18px]">
                  {formState?.errors?.program_id?.message}
                </small>
              )}
            </div>

            {type === "Harisenin" && (
              <div className="mt-1 relative">
                <div className={clsx("sm:flex grid sm:gap-3 gap-4 items-baseline")}>
                  <Listbox onChange={handleChangeDuration} value={selectedDuration}>
                    {({ open }) => (
                      <div className="relative flex-1 sm-only:text-sm">
                        {open ||
                          (selectedDuration && (
                            <div
                              className={clsx(
                                "absolute left-3 -top-2.5 bg-white px-2",
                                "text-xs",
                                formState?.errors?.duration?.message && "text-red"
                              )}
                            >
                              Durasi Cicilan
                            </div>
                          ))}
                        <Listbox.Button
                          className={clsx(
                            "py-4 px-3",
                            "rounded-sm  w-full",
                            "flex justify-between items-center",
                            open ? "border-green border-2" : "border-[rgba(58,53,65,0.23)] border",
                            formState?.errors?.duration?.message && "border-2 !border-red"
                          )}
                        >
                          {!open && (
                            <div className="pl-1.5">
                              {selectedDuration ? (
                                <span>
                                  {selectedDuration.duration === "Custom Durasi"
                                    ? "Custom Durasi"
                                    : `${selectedDuration.duration} Bulan`}
                                </span>
                              ) : (
                                "Durasi Cicilan"
                              )}
                            </div>
                          )}
                          {open && <span className="h-[1.5em]" />}
                          <span className={clsx(open && "text-green")}>
                            <AiOutlineCaretDown />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={React.Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options
                            className={clsx(
                              "w-full bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                              "absolute mt-1 overflow-auto z-[10] pl-0 py-2",
                              "divide-y divide-[rgba(58,53,65,0.23)]"
                            )}
                          >
                            {installmentDuration.map((duration, idx) => (
                              <Listbox.Option
                                key={idx}
                                value={duration}
                                className={({ active, selected }) =>
                                  clsx(
                                    "py-1.5 px-4 cursor-pointer",
                                    selected && "bg-green text-white",
                                    active && "bg-green-50"
                                  )
                                }
                              >
                                <span>
                                  {duration.duration === "Custom Durasi"
                                    ? "Custom Durasi"
                                    : `${duration.duration} Bulan`}
                                </span>
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    )}
                  </Listbox>

                  {isCustomDuration && (
                    <div
                      className={clsx(
                        "material_input relative sm:w-[180px] w-full sm-only:text-sm"
                      )}
                    >
                      <input
                        value={customDuration}
                        className={clsx(
                          customDuration && !formState?.errors?.duration?.message && "active",
                          formState?.errors?.duration?.message && "error"
                        )}
                        name="customDuration"
                        type="number"
                        pattern="[0-9]*"
                        onChange={handleChangeInput}
                      />
                      <div className="absolute right-4 top-4.5">Bulan</div>
                      <label className="label" htmlFor="customDuration">
                        <div className="text">Custom Durasi</div>
                      </label>
                    </div>
                  )}
                </div>

                {Boolean(formState?.errors?.duration?.message) && (
                  <small className="text-red absolute left-0 bottom-[-18px]">
                    {formState?.errors?.duration?.message}
                  </small>
                )}
              </div>
            )}
            <RegularButton
              className={clsx("sm:w-fit !w-full rounded-none")}
              variant="solid-blue"
              isFormik
              isSubmitting={isSubmitting}
            >
              Ajukan Cicilan
            </RegularButton>
            {errorMessage && <div className="text-red">{errorMessage}</div>}
          </div>
        </form>
      </Modal>
      <InstallmentSuccessModal show={successModal} onHide={() => setSuccessModal(false)} />
    </>
  )
}
